import { X } from "lucide-react"
import { Button } from "@/components/ui/Button"
import { Input } from "@/components/ui/Input"

interface BookmarkBoxProps {
  heading: string
  name: string
  description: string
  errors: string[]
  // False while the new bookmark is still being saved: there is nothing to
  // rename or remove yet.
  ready: boolean
  onNameChange: (name: string) => void
  onDescriptionChange: (description: string) => void
  onDone: () => void
  onRemove: () => void
}

// The box under the star, like Chrome's: "Bookmark added" or "Edit bookmark",
// the name (selected, so typing replaces it), a description, Remove and Done.
// Enter in either box is Done.
export default function BookmarkBox(props: BookmarkBoxProps) {
  const { heading, name, description, errors, ready, onNameChange, onDescriptionChange, onDone, onRemove } = props
  const onEnter = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && ready) {
      e.preventDefault()
      onDone()
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="text-base font-semibold text-foreground">{heading}</div>
        <Button variant="ghost" size="icon" aria-label="Close" className="h-7 w-7" onClick={onDone}>
          <X size={16} />
        </Button>
      </div>

      <div className="grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-2.5">
        <label htmlFor="bookmark-name" className="text-sm text-foreground">Name</label>
        <Input
          id="bookmark-name"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          onKeyDown={onEnter}
          onFocus={(e) => e.target.select()}
          autoFocus
        />
        <label htmlFor="bookmark-description" className="text-sm text-foreground">Description</label>
        <Input
          id="bookmark-description"
          value={description}
          placeholder="Optional"
          onChange={(e) => onDescriptionChange(e.target.value)}
          onKeyDown={onEnter}
        />
      </div>

      {errors.length > 0 && <div className="text-sm text-destructive">{errors.join(" ")}</div>}

      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" disabled={!ready} onClick={onRemove}>Remove</Button>
        <Button size="sm" disabled={!ready} onClick={onDone}>Done</Button>
      </div>
    </div>
  )
}
